// src/components/TreeFarmComponent.js
import React from "react";
import { Link } from "react-router-dom";
import "../styles/treeFarmComponent.css";

const defaultCopy = {
  // Compact variant text
  short:
    "Raised in the Wood River Valley for the Wood River Valley. Our trees are locally acclimated for health, longevity, and standout beauty—season after season.",
  support:
    "By nurturing trees from an early age in our local conditions, each one is fully acclimated before it reaches your landscape.",
  more:
    "With 50+ species and sizes to choose from—including evergreens like Bristlecone Pine and seasonal standouts such as Autumn Purple Ash—there's a perfect fit for every property.",

  // Default (editorial) variant text
  longLead:
    "The Wood River Valley's distinct climate is part of what makes this place so exceptional. Yet, this natural beauty comes with unique horticultural challenges—only select species of trees, shrubs, and flowers truly thrive here. Traditionally, trees brought in from outside the region struggle to adapt, often compromising their health and longevity.",
  longSupport:
    "That's why we've cultivated a more thoughtful approach: a dedicated tree farm right here in the heart of the Wood River Valley. By nurturing trees from an early age in our local conditions, we ensure each one is perfectly acclimated before it ever reaches your landscape. The result is healthier trees that last longer and look better, season after season.",
  moreFull:
    "Our curated selection includes everything from timeless evergreens like the Bristlecone Pine to striking seasonal varieties such as the Autumn Purple Ash. With over 50 unique species and sizes to choose from, there's something to complement every landscape vision. For a complete list of available trees, contact us today—we're happy to help you find the perfect fit.",

  // Images for editorial variant
  editorialImage1: "/images/tree/treeFarm/treeFarmLandscape.JPG",
  editorialImage2: "/images/tree/treeFarm/treeFXmasTree.JPG",
  editorialImage3: "/images/tree/treeFarm/treeFMultiPath.JPG",

  // Stats for compact variant
  kicker: "Locally Grown • Sun Valley Tough",
  stats: [
    { label: "Species", value: "50+" },
    { label: "Max Height", value: "30+ ft" },
    { label: "Years Growing", value: "35" },
  ],

  ctaPrimary: { label: "Explore the Tree Farm", to: "/TreeFarm" },

  imageHero: "/images/tree/treeFarm/treeFarmLandscape.JPG",
  imageAlt: "All Seasons Tree Farm in the Wood River Valley",
};

export default function TreeFarmComponent({
  variant = "default",          // "default" (editorial style) | "compact"
  copy = {},                    
  className = "",
  headingId,
}) {
  // Merge defaults with whatever the page passes
  const data = { ...defaultCopy, ...copy };
  const isCompact = variant === "compact";

  return (
    <section
      className={`treefarm treefarm--${variant} ${className}`}
      {...(headingId ? { "aria-labelledby": headingId } : {})}
    >
      {/* Compact variant - includes visual */}
      {isCompact ? (
        <div className="section-inner">
          <div className="treefarm__inner">
            {/* Visual */}
            <figure className="treefarm__visual">
              <div className="treefarm__image-wrap">
                <img src={data.imageHero} alt={data.imageAlt} loading="lazy" />
                {data.kicker && <div className="treefarm__badge">{data.kicker}</div>}

                {Array.isArray(data.stats) && data.stats.length > 0 && (
                  <div className="treefarm__chips">
                    {data.stats.map((s, i) => (
                      <div key={i} className="treefarm__chip">
                        <div className="treefarm__chip-value">{s.value}</div>
                        <div className="treefarm__chip-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <figcaption className="treefarm__caption">
              </figcaption>
            </figure>

            {/* Compact Content */}
            <div className="treefarm__content">
              <p className="treefarm__lead">{data.short}</p>
              {data.support && <p className="treefarm__support">{data.support}</p>}
              {data.more && <p className="treefarm__support">{data.more}</p>}

              <div className="treefarm__ctas">
                <Link to={data.ctaPrimary.to} className="btn-primary">
                  {data.ctaPrimary.label}
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Default variant - magazine grid layout */
        <div className="treefarm__editorial-wrapper">
          <div className="treefarm__editorial-grid">
            {/* Module 1: Text above, image below */}
            <div className="treefarm__module treefarm__module--1">
              <div className="treefarm__module-content">
                <p className="treefarm__module-text">
                  {data.longLead}
                </p>
              </div>
              <figure className="treefarm__module-image">
                <img 
                  src="/images/tree/treeFarm/treeFDifferent.JPG"
                  alt="Tree nursery aerial view"
                  loading="lazy"
                />
              </figure>
            </div>

            {/* Module 2: Large image above, text below */}
            <div className="treefarm__module treefarm__module--2">
              <figure className="treefarm__module-image treefarm__module-image--large">
                <img 
                  src="/images/tree/treeFarm/treeFPineLovely.JPG"
                  alt="Mature evergreen specimens"
                  loading="lazy"
                />
              </figure>
              <div className="treefarm__module-content">
                <p className="treefarm__module-text">
                  {data.longSupport}
                </p>
              </div>
            </div>

            {/* Module 3: Text split with image in middle */}
            <div className="treefarm__module treefarm__module--3">
              <div className="treefarm__module-split">
                <p className="treefarm__module-text treefarm__module-text--partial">
                  {data.moreFull.substring(0, Math.floor(data.moreFull.length / 2))}...
                </p>
                <figure className="treefarm__module-image treefarm__module-image--inline">
                  <img 
                    src="/images/tree/treeFarm/treeFFluffy.JPG"
                    alt="Tree variety selection"
                    loading="lazy"
                  />
                </figure>
                <p className="treefarm__module-text treefarm__module-text--partial">
                  ...{data.moreFull.substring(Math.floor(data.moreFull.length / 2))}
                </p>
              </div>
            </div>
          </div>

          {/* CTA at bottom - only show if ctaPrimary exists */}
          {data.ctaPrimary && data.ctaPrimary.to && (
            <div className="treefarm__editorial-cta">
              <Link to={data.ctaPrimary.to} className="btn-editorial">
                {data.ctaPrimary.label}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
}